import * as Styles from "@universityofmaryland/web-styles-library";
import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import { isPreferredReducedMotion } from "@universityofmaryland/web-utilities-library/accessibility";
import { debounce } from "@universityofmaryland/web-utilities-library/performance";
import { pause, play } from "@universityofmaryland/web-icons-library/controls";
const extractImageElement = (element) => {
  if (element instanceof HTMLImageElement) {
    return element;
  }
  if (element instanceof HTMLAnchorElement) {
    return element.querySelector("img");
  }
  return null;
};
const renderFirstFrame = (image, canvas, container) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;
  canvas.width = containerWidth;
  canvas.height = containerHeight;
  const imgRatio = image.naturalWidth / image.naturalHeight;
  const containerRatio = containerWidth / containerHeight;
  let drawWidth;
  let drawHeight;
  let offsetX = 0;
  let offsetY = 0;
  if (imgRatio > containerRatio) {
    drawHeight = containerHeight;
    drawWidth = image.naturalWidth * (containerHeight / image.naturalHeight);
    offsetX = (containerWidth - drawWidth) / 2;
  } else {
    drawWidth = containerWidth;
    drawHeight = image.naturalHeight * (containerWidth / image.naturalWidth);
    offsetY = (containerHeight - drawHeight) / 2;
  }
  ctx.clearRect(0, 0, containerWidth, containerHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};
const setupLazyLoading = (container, image, canvas, button, state, onLoad) => {
  if (typeof IntersectionObserver === "undefined") {
    state.isLoaded = true;
    button.style.display = "flex";
    onLoad();
    return null;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !state.isLoaded) {
          state.isLoaded = true;
          image.src = state.originalSrc;
          image.addEventListener(
            "load",
            () => {
              button.style.display = "flex";
              onLoad();
            },
            { once: true }
          );
          observer.disconnect();
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "50px"
      // Start loading slightly before visible
    }
  );
  observer.observe(container);
  return observer;
};
const applyGifToggle = (image, container, state) => {
  const canvas = document.createElement("canvas");
  const button = document.createElement("button");
  const setButtonPlay = () => {
    button.setAttribute("aria-label", "Pause");
    button.innerHTML = pause;
    canvas.style.opacity = "0";
    image.style.opacity = "1";
    state.isPlaying = true;
  };
  const setButtonPause = () => {
    button.setAttribute("aria-label", "Play");
    button.innerHTML = play;
    canvas.style.opacity = "1";
    image.style.opacity = "0";
    state.isPlaying = false;
  };
  const sizeCanvas = () => {
    if (!container) return;
    const img = container.querySelector("img");
    const canvasEl = container.querySelector("canvas");
    if (!canvasEl || !img) return;
    canvasEl.style.width = "100%";
    canvasEl.style.height = `${img.clientHeight}px`;
    canvasEl.style.position = "absolute";
    canvasEl.style.top = "0";
    canvasEl.style.left = "0";
    if (state.isLoaded && img.complete && img.naturalWidth > 0) {
      renderFirstFrame(img, canvasEl, container);
    }
  };
  button.setAttribute("type", "button");
  button.style.display = "none";
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (state.isPlaying) {
      setButtonPause();
    } else {
      setButtonPlay();
    }
  });
  container.appendChild(canvas);
  container.appendChild(button);
  image.style.opacity = "0";
  image.style.transition = "opacity 0.2s ease-in-out";
  canvas.style.opacity = "1";
  canvas.style.transition = "opacity 0.2s ease-in-out";
  const captureFirstFrame = () => {
    sizeCanvas();
    renderFirstFrame(image, canvas, container);
  };
  if (image.complete && image.naturalWidth > 0) {
    captureFirstFrame();
  } else {
    image.addEventListener("load", captureFirstFrame, { once: true });
  }
  setupLazyLoading(container, image, canvas, button, state, () => {
    sizeCanvas();
    if (isPreferredReducedMotion()) {
      setButtonPause();
    } else {
      setButtonPlay();
    }
  });
  window.addEventListener(
    "resize",
    debounce(() => {
      sizeCanvas();
    }, 50)
  );
};
const createImageGif = ({
  element
}) => {
  const image = extractImageElement(element);
  if (!image) {
    throw new Error("No valid image element found");
  }
  const state = {
    isLoaded: false,
    isPlaying: false,
    originalSrc: image.src
  };
  const isAnchor = element instanceof HTMLAnchorElement;
  const container = isAnchor ? element : document.createElement("div");
  const composite = new ElementBuilder(container).styled(Styles.element.asset.gif.toggle).withModifier((el) => {
    if (!isAnchor) {
      el.appendChild(element);
    }
    applyGifToggle(image, el, state);
  }).build();
  return composite;
};
export {
  createImageGif
};
//# sourceMappingURL=gif.js.map
